import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Box,
  Typography,
  Button,
  Spinner,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@glplus/glpweb-dsl';
import { useCapabilities } from '../lib/useCapabilities';
import {
  adminApiClient,
  type ManagedVendorDetail,
  type VendorApplicationContext,
  type VendorBusinessInfo,
} from '../lib/adminApiClient';
import { NEUTRAL_BTN, DANGER_BTN } from '../lib/buttonStyles';
import { StoreChip, VendorStatusChip } from '../components/VendorChips';

/**
 * Manage vendors — detail (Figma "Manage vendors · Detail"). Six cards mirroring
 * how the application gathered the data (Business · Point of contact · Location &
 * hours · Systems & eligibility · Your Interests · Storefront & setup status).
 *
 * An approved vendor is NOT live to shoppers until it finishes the §9 setup
 * wizard — the Storefront card shows that state. v1 admin actions are Edit
 * profile + Suspend / Reactivate only. No UPC anywhere (vendors carry none).
 */

const PRIMARY_BTN = {
  backgroundColor: '#f06500',
  color: '#fff',
  '&:hover': { backgroundColor: '#d85b00' },
};
const DOW = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const DOW_SHORT: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu',
  friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
};

function addressLine(v: ManagedVendorDetail): string {
  const a = v.address;
  if (!a) return '—';
  const parts = [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(', '), a.postalCode];
  return parts.filter(Boolean).join(', ') || '—';
}

function hoursSummary(hours: Record<string, unknown> | null): string {
  if (!hours || typeof hours !== 'object') return '—';
  const parts: string[] = [];
  for (const day of DOW) {
    const h = hours[day] as { open?: string; close?: string; closed?: boolean } | undefined;
    if (!h) continue;
    parts.push(h.closed ? `${DOW_SHORT[day]} closed` : `${DOW_SHORT[day]} ${h.open ?? '?'}–${h.close ?? '?'}`);
  }
  return parts.length ? parts.join('  ·  ') : '—';
}

function commissionLine(v: ManagedVendorDetail): string {
  const c = v.commissionSchedules[0];
  if (!c) return '—';
  const date = new Date(c.effectiveFrom).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
  return `${(c.rateBps / 100).toFixed(1)}% (${c.rateBps} bps)  ·  since ${date}`;
}

function displayStatus(status: string): string {
  switch (status) {
    case 'active': return 'Live to shoppers';
    case 'onboarding':
    case 'pending': return 'In setup — not yet live to shoppers';
    case 'suspended': return 'Suspended — hidden from shoppers';
    case 'inactive':
    case 'deactivated': return 'Inactive — not shown to shoppers';
    case 'blocked': return 'Blocked';
    default: return status;
  }
}

function teamLine(v: ManagedVendorDetail): string {
  if (!v.memberships.length) return '—';
  return v.memberships
    .map((m) => `${m.user?.displayName ?? m.user?.email ?? 'member'} (${m.role})`)
    .join('  ·  ');
}

function Centered({ children }: { children: React.ReactNode }) {
  return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>{children}</Box>;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9aa3ab', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: '#374151' }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: 3,
        backgroundColor: '#fff',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.75,
      }}
    >
      <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>{title}</Typography>
      {children}
    </Box>
  );
}

export default function ManageVendorDetail() {
  const { id = '' } = useParams();
  const caps = useCapabilities();
  const canView = caps.has('vendor:read');
  const canManage = caps.has('vendor:manage');
  const navigate = useNavigate();

  const [vendor, setVendor] = useState<ManagedVendorDetail | null>(null);
  const [appCtx, setAppCtx] = useState<VendorApplicationContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'edit' | 'suspend' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApiClient.getVendor(id);
      setVendor(res.vendor);
      setAppCtx(res.application);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load vendor');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  const bi: VendorBusinessInfo = appCtx?.businessInfo ?? {};

  if (caps.loading || loading)
    return (
      <Centered>
        <Spinner color="brand" />
      </Centered>
    );
  if (!canView) return <Alert severity="error">You don’t have permission to view vendors.</Alert>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!vendor) return <Alert severity="warning">Vendor not found.</Alert>;

  const joined = new Date(vendor.createdAt).toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  return (
    <Box>
      <Box
        component="button"
        onClick={() => navigate('/manage-vendors')}
        sx={{
          border: 'none', background: 'none', cursor: 'pointer', p: 0, mb: 1.5,
          color: '#15803d', fontWeight: 600, fontSize: '14px',
        }}
      >
        ‹ Manage vendors
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.75 }}>
            {vendor.businessName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <StoreChip businessType={vendor.businessType} />
            <VendorStatusChip status={vendor.status} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {[vendor.address?.city, vendor.address?.state].filter(Boolean).join(', ') || '—'} · joined {joined}
            </Typography>
          </Box>
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {vendor.status === 'suspended' ? (
              <Button
                ariaLabel="Reactivate vendor"
                buttonText="Reactivate"
                variant="outlined"
                size="small"
                sx={NEUTRAL_BTN}
                onClick={async () => {
                  const res = await adminApiClient.reactivateVendor(vendor.vendorId);
                  setVendor({ ...vendor, status: res.vendor.status });
                }}
              />
            ) : (
              <Button
                ariaLabel="Suspend vendor"
                buttonText="Suspend"
                variant="outlined"
                size="small"
                sx={DANGER_BTN}
                onClick={() => setDialog('suspend')}
              />
            )}
            <Button
              ariaLabel="Edit vendor"
              buttonText="Edit"
              variant="contained"
              size="small"
              sx={PRIMARY_BTN}
              onClick={() => setDialog('edit')}
            />
          </Box>
        )}
      </Box>

      {/* Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Card title="Business">
          <Field label="LEGAL NAME" value={vendor.businessName} />
          <Field label="STORE TYPE" value={<StoreChip businessType={vendor.businessType} />} />
          <Field label="WEBSITE / SOCIAL" value={bi.website} />
          <Field label="# OF LOCATIONS" value={bi.numberOfLocations} />
        </Card>

        <Card title="Point of contact">
          <Field label="CONTACT NAME" value={bi.contactName ?? vendor.owner?.displayName} />
          <Field label="ROLE" value={bi.role} />
          <Field label="PHONE" value={bi.contactPhone} />
          <Field label="EMAIL" value={bi.contactEmail ?? vendor.owner?.email} />
        </Card>

        <Card title="Location & hours">
          <Field label="ADDRESS" value={addressLine(vendor)} />
          <Field label="HOURS" value={hoursSummary(vendor.hours)} />
        </Card>

        <Card title="Systems & eligibility">
          <Field label="POS SYSTEM" value={vendor.posSystem} />
          <Field label="PRODUCT CONNECTION" value={vendor.posSystem === 'square_oauth' ? 'Square OAuth · live sync' : vendor.posSystem} />
          <Field label="SNAP / EBT" value={vendor.snapAuthorized ? 'Accepts SNAP / EBT' : 'Not a SNAP retailer'} />
          <Field label="FNS NUMBER" value={bi.fnsNumber} />
        </Card>

        <Card title="Your Interests">
          <Field label="APPROX PRODUCTS" value={bi.approxProductCount} />
          <Field label="WHAT MATTERS MOST" value={bi.interests?.length ? bi.interests.join('  ·  ') : undefined} />
        </Card>

        <Card title="Storefront & setup status">
          <Field label="DISPLAY STATUS" value={displayStatus(vendor.status)} />
          <Field label="COMMISSION" value={commissionLine(vendor)} />
          <Field label="STRIPE CONNECT" value={vendor.stripeConnectAccountId ? `Connected · ${vendor.stripeConnectAccountId}` : 'Not connected'} />
          <Field label="OWNER" value={vendor.owner ? `${vendor.owner.displayName ?? '—'}  ·  ${vendor.owner.email}` : undefined} />
          <Field label="TEAM" value={teamLine(vendor)} />
        </Card>
      </Box>

      {dialog === 'edit' && (
        <EditVendorDialog
          vendor={vendor}
          onClose={() => setDialog(null)}
          onSaved={(updated) => {
            setVendor({ ...vendor, ...updated });
            setDialog(null);
          }}
        />
      )}
      {dialog === 'suspend' && (
        <SuspendVendorDialog
          vendor={vendor}
          onClose={() => setDialog(null)}
          onSuspended={(status) => {
            setVendor({ ...vendor, status });
            setDialog(null);
          }}
        />
      )}
    </Box>
  );
}

// ─── Edit profile dialog ───────────────────────────────────────────────────────
function EditVendorDialog({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: ManagedVendorDetail;
  onClose: () => void;
  onSaved: (updated: Partial<ManagedVendorDetail>) => void;
}) {
  const [businessName, setBusinessName] = useState(vendor.businessName);
  const [businessType, setBusinessType] = useState(vendor.businessType);
  const [categories, setCategories] = useState((vendor.categories ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const cats = categories.split(',').map((c) => c.trim()).filter(Boolean);
      const res = await adminApiClient.updateVendor(vendor.vendorId, {
        businessName: businessName.trim(),
        businessType: businessType.trim(),
        categories: cats,
      });
      onSaved({
        businessName: res.vendor.businessName,
        businessType: res.vendor.businessType,
        categories: res.vendor.categories,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth paperBorderRadius={16}>
      <DialogTitle>
        <Typography sx={{ fontWeight: 800, fontSize: '18px' }}>Edit vendor</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Update this vendor’s profile. Changes are audited; name / store-type edits may re-flag
          for verification.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Business name" ariaLabel="Business name" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
          <TextField label="Store type" ariaLabel="Store type" value={businessType} onChange={(e) => setBusinessType(e.target.value)} />
          <TextField label="Categories (comma-separated)" ariaLabel="Categories" value={categories} onChange={(e) => setCategories(e.target.value)} />
          {err && <Alert severity="error">{err}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button ariaLabel="Cancel" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={onClose} />
        <Button ariaLabel="Save changes" buttonText={saving ? 'Saving…' : 'Save changes'} variant="contained" size="small" disabled={saving || !businessName.trim()} sx={PRIMARY_BTN} onClick={() => void save()} />
      </DialogActions>
    </Dialog>
  );
}

// ─── Suspend dialog ─────────────────────────────────────────────────────────────
function SuspendVendorDialog({
  vendor,
  onClose,
  onSuspended,
}: {
  vendor: ManagedVendorDetail;
  onClose: () => void;
  onSuspended: (status: ManagedVendorDetail['status']) => void;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const suspend = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await adminApiClient.suspendVendor(vendor.vendorId, reason.trim() || undefined);
      onSuspended(res.vendor.status);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to suspend');
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth paperBorderRadius={16}>
      <DialogTitle>
        <Typography sx={{ fontWeight: 800, fontSize: '18px' }}>Suspend {vendor.businessName}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          Hides the storefront from shoppers and pauses the vendor. Reversible — reactivate anytime.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Reason (optional)" ariaLabel="Suspension reason" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          {err && <Alert severity="error">{err}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button ariaLabel="Cancel" buttonText="Cancel" variant="outlined" size="small" sx={NEUTRAL_BTN} onClick={onClose} />
        <Button ariaLabel="Suspend vendor" buttonText={saving ? 'Suspending…' : 'Suspend vendor'} variant="outlined" size="small" disabled={saving} sx={DANGER_BTN} onClick={() => void suspend()} />
      </DialogActions>
    </Dialog>
  );
}
